import {type KeyboardEvent, useMemo} from 'react'

import classes from './run-map.module.css'
import {LATE_LEG_COLOR, ROUTE_COLOR} from './run-map-colors'
import {fitProjection, formatDuration, formatMiles} from './run-map-logic'
import type {RunMapSurfaceProps} from './run-map-surface'

const WIDTH = 800
const HEIGHT = 480
const PADDING = 48
const PIN_RADIUS = 9

export function RunMapSchematic({pins, yardMarkers, legs, onOpenJob}: RunMapSurfaceProps) {
    const project = useMemo(
        () =>
            fitProjection(
                [
                    ...pins.map((pin) => pin.position),
                    ...yardMarkers.map((marker) => marker.position),
                ],
                WIDTH,
                HEIGHT,
                PADDING,
            ),
        [pins, yardMarkers],
    )

    const openOnKey = (jobId: string) => (event: KeyboardEvent<SVGGElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpenJob(jobId)
        }
    }

    const orderedPins = [...pins].sort(
        (a, b) => Number(a.stopNumber != null) - Number(b.stopNumber != null),
    )

    return (
        <svg
            className={classes['schematic']}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            role="group"
            aria-label="Schematic run map"
            data-testid="run-map-schematic"
        >
            <rect
                x={0}
                y={0}
                width={WIDTH}
                height={HEIGHT}
                className={classes['schematicBackground']}
            />
            {legs.map((leg) => {
                const from = project(leg.from)
                const to = project(leg.to)
                const late = leg.lateByMinutes != null
                const mid = {x: (from.x + to.x) / 2, y: (from.y + to.y) / 2}
                const label = `${formatMiles(leg.miles)} · ${formatDuration(leg.minutes)}${
                    leg.method === 'estimate' ? ' approx.' : ''
                }`
                return (
                    <g key={`${leg.key}:${leg.toStop?.number ?? 'yard'}`} data-testid="run-map-leg">
                        <title>{`${leg.fromLabel} to ${leg.toLabel}: ${label}${
                            late ? ', does not fit' : ''
                        }`}</title>
                        <line
                            x1={from.x}
                            y1={from.y}
                            x2={to.x}
                            y2={to.y}
                            stroke={late ? LATE_LEG_COLOR.css : ROUTE_COLOR.css}
                            strokeWidth={late ? 3 : 2.5}
                            strokeDasharray={late ? '8 5' : undefined}
                            strokeLinecap="round"
                        />
                        <text
                            x={mid.x}
                            y={mid.y - 6}
                            textAnchor="middle"
                            className={late ? classes['legLabelLate'] : classes['legLabel']}
                        >
                            {late ? `${label} late` : label}
                        </text>
                    </g>
                )
            })}
            {yardMarkers.map((marker) => {
                const {x, y} = project(marker.position)
                return (
                    <g key={marker.yard.id} aria-label={`${marker.yard.name} yard`} role="img">
                        <path
                            d={`M ${x - 10} ${y + 9} L ${x - 10} ${y - 2} L ${x} ${y - 11} L ${
                                x + 10
                            } ${y - 2} L ${x + 10} ${y + 9} Z`}
                            fill={marker.color.css}
                            className={classes['yardMarker']}
                        />
                        <text x={x} y={y + 24} textAnchor="middle" className={classes['yardLabel']}>
                            {marker.yard.name}
                        </text>
                    </g>
                )
            })}
            {orderedPins.map((pin) => {
                const {x, y} = project(pin.position)
                const filled = pin.job.staffing === 'staffed'
                const radius = pin.stopNumber != null ? PIN_RADIUS + 3 : PIN_RADIUS
                return (
                    <g
                        key={pin.job.id}
                        role="button"
                        tabIndex={0}
                        aria-label={pin.ariaLabel}
                        className={classes['pin']}
                        opacity={pin.dimmed ? 0.4 : 1}
                        onClick={() => onOpenJob(pin.job.id)}
                        onKeyDown={openOnKey(pin.job.id)}
                        data-testid="run-map-pin"
                        data-staffing={pin.job.staffing}
                    >
                        <title>{pin.ariaLabel}</title>
                        <circle
                            cx={x}
                            cy={y}
                            r={radius}
                            fill={filled ? pin.color.css : 'var(--mantine-color-body)'}
                            stroke={pin.color.css}
                            strokeWidth={3}
                        />
                        {pin.stopNumber != null ? (
                            <text
                                x={x}
                                y={y + 4}
                                textAnchor="middle"
                                className={
                                    filled ? classes['stopNumberOnFill'] : classes['stopNumber']
                                }
                            >
                                {pin.stopNumber}
                            </text>
                        ) : null}
                    </g>
                )
            })}
        </svg>
    )
}
